import { debounce } from 'lodash-es';
import {
  useCallback,
  useEffect,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import { CustomActions, CustomIntents } from './tooltip/CustomActions.js';
import { getStratoLogger } from '../../../../core/sdk/sdk-logger.js';
import { Divider } from '../../../../layouts/divider/Divider.js';
import type { Point } from '../../types/point.js';
import { getCustomContextMenuItems } from '../interactions-overlay/utils/get-custom-context-menu-items.js';
import { OverlayTooltip } from '../overlay-tooltip/OverlayTooltip.js';
import { divider } from '../overlay-tooltip/tooltip.sty.js';

type ActionsMenu = ReturnType<typeof getCustomContextMenuItems>;
export type SelectionTooltipWithAsyncActionsProps = {
  actions: () => Promise<ActionsMenu> | undefined;
  position: Point;
  zoomEnabled: boolean;
  header: ReactElement;
  range: [number, number] | [Date, Date];
  selectFinished: boolean;
};

const DEBOUNCING_TIME = 150;
const emptyActionsMenu: ActionsMenu = { actions: [], intents: [] };

export const SelectionTooltipWithAsyncActions = (
  props: PropsWithChildren<SelectionTooltipWithAsyncActionsProps>,
) => {
  const [actionsMenu, setActionsMenu] = useState<ActionsMenu>(emptyActionsMenu);
  const logger = getStratoLogger('SelectionTooltipWithAsyncActions');
  const fetchActions = useCallback(
    debounce(
      (actions: SelectionTooltipWithAsyncActionsProps['actions']) => {
        const promise = actions();

        if (promise) {
          promise
            .then((result) => {
              if (result.actions.length || result.intents.length) {
                setActionsMenu(result);
              }
            })
            .catch((error) => {
              logger.error('Failed to load select series actions', error);
            });
        }
      },
      DEBOUNCING_TIME,
      { leading: false, trailing: true },
    ),
    [logger],
  );

  useEffect(() => {
    if (!!actionsMenu.actions.length || !!actionsMenu.intents.length) {
      setActionsMenu(emptyActionsMenu);
    }

    props.actions && fetchActions(props.actions);

    return () => {
      fetchActions.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.range[0], props.range[1]]);

  const hasActions = !!actionsMenu.actions.length;
  const hasIntents = !!actionsMenu.intents.length;

  return (
    <OverlayTooltip
      isInteractive={props.selectFinished}
      absoluteX={props.position.x}
      absoluteY={props.position.y}
    >
      {props.header}
      {(!!props.children || hasActions || hasIntents) && (
        <>
          <Divider
            orientation="horizontal"
            key="headerDivider"
            className={divider}
          />
          <div
            style={{
              maxHeight: '96px',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {props.children}
            {hasActions ? (
              <CustomActions actions={actionsMenu.actions} />
            ) : null}
            {hasIntents && (hasActions || !!props.children) ? (
              <Divider
                orientation="horizontal"
                key="actionsDivider"
                className={divider}
              />
            ) : null}
            {hasIntents ? (
              <CustomIntents intents={actionsMenu?.intents} />
            ) : null}
          </div>
        </>
      )}
    </OverlayTooltip>
  );
};
